// ui/services/trueTodoAdapter.ts
// ✅ 覆蓋全文
//
// Goal
// - Normalize heterogeneous "todo-like" docs into TrueTodoItem[] for dashboard KPI.
// - Defensive: never throw (bad docs -> excluded).
//
// Project rules
// ✅ NOT_STARTED 一律算 kevin（法務）
// ✅ coo@lg.app / psd@lg.app 對應 ops / gm
// ✅ key 必須穩定（同一個簽核在 request/flow 來源要能 dedupe）
// ✅ 保留 TEMPLATE_*（不要把範本模組洗掉）
//
// Type contract is defined in ui/types/trueTodo.ts (DO NOT invent fields):
// - TrueTodoItem: { source,id,key,module,title,targetUsers, status?, docNo?, docCode?, createdAt?, updatedAt?, raw? }

import type { TrueTodoExcluded, TrueTodoItem, TrueTodoSource } from "../types/trueTodo";



// ✅ Re-export canonical types for other modules (e.g. trueTodoAggregator.ts)
export type { TrueTodoExcluded, TrueTodoItem, TrueTodoSource } from "../types/trueTodo";
type DocRow = { id: string; data: any };
type PushExcluded = (ex: TrueTodoExcluded) => void;

type UserKey = "kevin" | "ops" | "gm";

const DISPLAY_NAME: Record<UserKey, string> = {
  kevin: "法務",
  ops: "營運長",
  gm: "總經理",
};

function up(v: any): string {
  return String(v ?? "").trim().toUpperCase();
}

function low(v: any): string {
  return String(v ?? "").trim().toLowerCase();
}

function isClosedStatus(v: any): boolean {
  const s = up(v);
  return (
    s === "CLOSED" ||
    s === "APPROVED" ||
    s === "REJECTED" ||
    s === "CANCELLED" ||
    s === "CANCELED" ||
    s === "ARCHIVED" ||
    s === "DONE" ||
    s === "COMPLETED" ||
    s === "FULLY_APPROVED"
  );
}

function isPendingStageStatus(v: any): boolean {
  const s = up(v);
  return s === "PENDING" || s === "WAITING" || s === "IN_PROGRESS" || s === "OPEN";
}

function normalizeModule(input: any): string {
  const m = up(input);
  if (!m) return "UNKNOWN";

  // ✅ Preserve TEMPLATE_* as-is (dashboard uses startsWith("TEMPLATE"))
  if (m.startsWith("TEMPLATE")) return m;

  // Normalize common aliases
  if (m === "CONTRACT" || m.includes("CONTRACT_REVIEW")) return "CONTRACT_REVIEW";
  if (m === "SEAL" || m.includes("SEAL")) return "SEAL_REQUEST";
  if (m === "OFFICIAL" || m.includes("OFFICIAL") || m.includes("GOV_CORRESP")) return "OFFICIAL_REPLY";
  if (m === "LAW_INFO" || m.includes("COMPLIANCE") || m.includes("INTEL")) return "LAW_INFO";
  if (m.includes("PROJECT")) return "PROJECT_CONTROL";
  if (m.includes("GOVERNANCE")) return "GOVERNANCE_CORE";
  if (m.includes("CASE") || m.includes("DISPUTE")) return "CASE_DISPUTE";

  return m;
}

function pickTitle(raw: any): string {
  const t =
    raw?.title ||
    raw?.subject ||
    raw?.name ||
    raw?.docTitle ||
    raw?.contractTitle ||
    raw?.summary ||
    raw?.docNo ||
    raw?.docCode ||
    "";
  return String(t ?? "").trim();
}

function normalizeUserKey(rawKey: any): UserKey | null {
  const k = low(rawKey);
  if (!k) return null;

  if (k === "kevin" || k === "law" || k === "legal") return "kevin";
  if (k === "ops" || k === "coo" || k === "operation" || k === "operations") return "ops";
  if (k === "gm" || k === "ceo" || k === "president" || k === "psd") return "gm";

  return null;
}

function emailToUserKey(email: any): UserKey | null {
  const e = low(email);
  if (!e) return null;

  // ✅ new superusers
  if (e === "coo@lg.app") return "ops";
  if (e === "psd@lg.app") return "gm";

  // loose fallback
  if (e.includes("legal")) return "kevin";
  if (e.includes("coo") || e.includes("ops")) return "ops";
  if (e.includes("gm") || e.includes("psd") || e.includes("ceo")) return "gm";

  return null;
}

function stageKeyToUserKey(stageKey: any): UserKey | null {
  const s = up(stageKey);
  if (!s) return null;

  if (s.includes("LEGAL") || s.includes("LAW")) return "kevin";
  if (s.includes("OPS") || s.includes("COO") || s.includes("OPERATION")) return "ops";
  if (s.includes("GM") || s.includes("CEO") || s.includes("PRESIDENT") || s.includes("PSD")) return "gm";

  return null;
}

function mkTargetUsers(keys: Array<UserKey | null | undefined>): TrueTodoItem["targetUsers"] {
  const seen = new Set<UserKey>();
  const out: TrueTodoItem["targetUsers"] = [];
  for (const k of keys) {
    if (!k) continue;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ userKey: k, displayName: DISPLAY_NAME[k] });
  }
  return out;
}

function pushEx(pushExcluded: PushExcluded | undefined, source: TrueTodoSource, id: string, reason: TrueTodoExcluded["reason"], raw?: any) {
  if (!pushExcluded) return;
  try {
    pushExcluded({ source, id, reason, raw });
  } catch {
    // never throw
  }
}

/**
 * Derive "who should handle now" from an approval flow-like document.
 * Returns { userKey, stageKey } or null.
 */
function derivePendingFromFlow(flowRaw: any): { userKey: UserKey; stageKey: string } | null {
  const statusUpper = up(flowRaw?.status || flowRaw?.state || flowRaw?.flowStatus || flowRaw?.approvalStatus);

  // ✅ Project rule: NOT_STARTED => kevin
  if (statusUpper === "NOT_STARTED") return { userKey: "kevin", stageKey: "NOT_STARTED" };
  if (isClosedStatus(statusUpper)) return null;

  // direct hints
  const directUserKey =
    normalizeUserKey(flowRaw?.pendingUserKey) ||
    normalizeUserKey(flowRaw?.pendingUser) ||
    normalizeUserKey(flowRaw?.assigneeUserKey) ||
    normalizeUserKey(flowRaw?.currentAssigneeKey) ||
    emailToUserKey(flowRaw?.pendingUserEmail) ||
    emailToUserKey(flowRaw?.assigneeEmail);

  const directStageKey = String(flowRaw?.currentStageKey || flowRaw?.stageKey || flowRaw?.pendingStageKey || "").trim();

  if (directUserKey) return { userKey: directUserKey, stageKey: directStageKey || "PENDING" };

  if (directStageKey) {
    const uk = stageKeyToUserKey(directStageKey);
    if (uk) return { userKey: uk, stageKey: directStageKey };
  }

  // stages array
  const stages: any[] = Array.isArray(flowRaw?.stages)
    ? flowRaw.stages
    : Array.isArray(flowRaw?.approvalStages)
      ? flowRaw.approvalStages
      : Array.isArray(flowRaw?.flowStages)
        ? flowRaw.flowStages
        : [];

  // stage NOT_STARTED => kevin (project rule)
  const notStartedStage = stages.find((s) => up(s?.status || s?.state) === "NOT_STARTED");
  if (notStartedStage) return { userKey: "kevin", stageKey: String(notStartedStage?.stageKey || notStartedStage?.key || "NOT_STARTED") };

  const pending = stages.find((s) => isPendingStageStatus(s?.status || s?.state || s?.result || s?.approvalStatus));
  if (pending) {
    const sk = String(pending?.stageKey || pending?.key || pending?.id || "").trim();
    const uk =
      stageKeyToUserKey(sk) ||
      normalizeUserKey(pending?.userKey) ||
      normalizeUserKey(pending?.assigneeUserKey) ||
      emailToUserKey(pending?.assigneeEmail);
    if (uk) return { userKey: uk, stageKey: sk || "PENDING" };
  }

  // last resort for open flows
  if (statusUpper === "PENDING" || statusUpper === "WAITING" || statusUpper === "IN_PROGRESS" || statusUpper === "OPEN") {
    return { userKey: "kevin", stageKey: "PENDING" };
  }

  return null;
}

function getTargetIdFromFlowDoc(flowDocId: string, flowRaw: any, moduleNorm: string): string {
  const rawTid =
    flowRaw?.targetId ||
    flowRaw?.requestId ||
    flowRaw?.approvalRequestId ||
    flowRaw?.entityId ||
    flowRaw?.docId ||
    flowRaw?.target?.id ||
    flowRaw?.target?.targetId;

  if (rawTid) return String(rawTid);

  // common: flow.id = `${module}_${targetId}`
  const id = String(flowDocId || "");
  const prefix = moduleNorm ? `${moduleNorm}_` : "";
  if (prefix && id.startsWith(prefix)) return id.slice(prefix.length);

  // fallback: split at first underscore
  if (id.includes("_")) return id.split("_").slice(1).join("_") || id;

  return id;
}

// ---------------------------------------------------------------------------
// Exported adapters (signatures must match trueTodoAggregator.ts)
// ---------------------------------------------------------------------------

export function adaptRawTodoDocs(docs: DocRow[], pushExcluded?: PushExcluded): TrueTodoItem[] {
  const out: TrueTodoItem[] = [];
  for (const row of docs || []) {
    const id = String(row?.id || "").trim();
    if (!id) continue;

    const raw = row?.data || {};
    const statusUpper = up(raw?.status);
    if (statusUpper && isClosedStatus(statusUpper)) {
      pushEx(pushExcluded, "RAW_TODO", id, "CLOSED", raw);
      continue;
    }

    const module = normalizeModule(raw?.module || raw?.moduleKey || raw?.sourceModule);
    const title = pickTitle(raw) || "(待辦)";

    const uk =
      normalizeUserKey(raw?.ownerUserKey) ||
      normalizeUserKey(raw?.assigneeUserKey) ||
      normalizeUserKey(raw?.userKey) ||
      emailToUserKey(raw?.ownerEmail) ||
      emailToUserKey(raw?.assigneeEmail) ||
      "kevin";

    const key = `RAW:${module}:${id}:${uk}`;
    out.push({
      source: "RAW_TODO",
      id,
      key,
      module,
      title,
      status: String(raw?.status || "OPEN"),
      createdAt: raw?.createdAt,
      updatedAt: raw?.updatedAt || raw?.createdAt,
      docNo: raw?.docNo ? String(raw.docNo) : undefined,
      docCode: raw?.docCode ? String(raw.docCode) : undefined,
      targetUsers: mkTargetUsers([uk]),
      raw,
    });
  }
  return out;
}

export function adaptApprovalRequestDocs(
  docs: DocRow[],
  pushExcluded?: PushExcluded,
  flowByKey?: Record<string, any>
): TrueTodoItem[] {
  const out: TrueTodoItem[] = [];

  for (const row of docs || []) {
    const id = String(row?.id || "").trim();
    if (!id) continue;

    const raw = row?.data || {};
    const module = normalizeModule(raw?.module || raw?.moduleKey || raw?.sourceModule);
    const title = pickTitle(raw) || "(簽核請求)";
    const statusUpper = up(raw?.status || raw?.state || raw?.requestStatus);

    if (statusUpper && isClosedStatus(statusUpper)) {
      pushEx(pushExcluded, "APPROVAL_REQUEST", id, "CLOSED", raw);
      continue;
    }

    const flow =
      (flowByKey &&
        (flowByKey[id] ||
          flowByKey[`${module}_${id}`] ||
          flowByKey[raw?.flowId] ||
          flowByKey[raw?.approvalFlowId] ||
          flowByKey[raw?.targetId])) ||
      null;

    const pendingFromFlow = flow ? derivePendingFromFlow(flow) : null;

    // request-side hints
    const reqUserKey =
      normalizeUserKey(raw?.pendingUserKey) ||
      normalizeUserKey(raw?.assigneeUserKey) ||
      emailToUserKey(raw?.pendingUserEmail) ||
      emailToUserKey(raw?.assigneeEmail) ||
      stageKeyToUserKey(raw?.stageKey);

    const uk = (pendingFromFlow?.userKey || reqUserKey || null) as UserKey | null;

    if (!uk) {
      pushEx(pushExcluded, "APPROVAL_REQUEST", id, "NOT_ACTIONABLE", raw);
      continue;
    }

    const key = `FLOW:${module}:${id}:${uk}`;

    out.push({
      source: "APPROVAL_REQUEST",
      id,
      key,
      module,
      title,
      status: String(raw?.status || "PENDING"),
      createdAt: raw?.createdAt,
      updatedAt: raw?.updatedAt || raw?.createdAt,
      docNo: raw?.docNo ? String(raw.docNo) : undefined,
      docCode: raw?.docCode ? String(raw.docCode) : undefined,
      targetUsers: mkTargetUsers([uk]),
      raw: flow ? { ...raw, __flow: flow } : raw,
    });
  }

  return out;
}

export function adaptApprovalFlowDocs(
  docs: DocRow[],
  pushExcluded?: PushExcluded,
  requestByKey?: Record<string, any>
): TrueTodoItem[] {
  const out: TrueTodoItem[] = [];

  for (const row of docs || []) {
    const flowDocId = String(row?.id || "").trim();
    if (!flowDocId) continue;

    const flowRaw = row?.data || {};
    const module = normalizeModule(flowRaw?.module || flowRaw?.moduleKey || flowRaw?.sourceModule);

    const statusUpper = up(flowRaw?.status || flowRaw?.state || flowRaw?.flowStatus || flowRaw?.approvalStatus);
    if (statusUpper && isClosedStatus(statusUpper)) {
      pushEx(pushExcluded, "APPROVAL_FLOW", flowDocId, "CLOSED", flowRaw);
      continue;
    }

    const targetId = getTargetIdFromFlowDoc(flowDocId, flowRaw, module);

    const req =
      (requestByKey &&
        (requestByKey[targetId] ||
          requestByKey[`${module}_${targetId}`] ||
          requestByKey[flowRaw?.requestId] ||
          requestByKey[flowRaw?.approvalRequestId])) ||
      null;

    const pending = derivePendingFromFlow(flowRaw);
    if (!pending) {
      pushEx(pushExcluded, "APPROVAL_FLOW", flowDocId, "NOT_ACTIONABLE", flowRaw);
      continue;
    }

    if (!req) {
      // not fatal (KPI can still be derived)
      pushEx(pushExcluded, "APPROVAL_FLOW", flowDocId, "MISSING_REQUEST", { ...flowRaw, __targetId: targetId, __module: module });
    }

    const title = pickTitle(req || flowRaw) || "(簽核流轉)";
    const uk = pending.userKey;

    const key = `FLOW:${module}:${targetId}:${uk}`;

    out.push({
      source: "APPROVAL_FLOW",
      id: flowDocId,
      key,
      module,
      title,
      status: String(flowRaw?.status || "PENDING"),
      createdAt: flowRaw?.createdAt || req?.createdAt,
      updatedAt: flowRaw?.updatedAt || flowRaw?.createdAt || req?.updatedAt || req?.createdAt,
      docNo: (req?.docNo ?? flowRaw?.docNo) ? String(req?.docNo ?? flowRaw?.docNo) : undefined,
      docCode: (req?.docCode ?? flowRaw?.docCode) ? String(req?.docCode ?? flowRaw?.docCode) : undefined,
      targetUsers: mkTargetUsers([uk]),
      raw: {
        ...(req || {}),
        ...(flowRaw || {}),
        __flowDocId: flowDocId,
        __targetId: targetId,
      },
    });
  }

  return out;
}

export function dedupeTrueTodos(items: TrueTodoItem[]): TrueTodoItem[] {
  const priority: Record<TrueTodoSource, number> = {
    APPROVAL_FLOW: 30,
    APPROVAL_REQUEST: 20,
    RAW_TODO: 10,
  };

  const map = new Map<string, TrueTodoItem>();

  const mergeTargets = (a: TrueTodoItem["targetUsers"], b: TrueTodoItem["targetUsers"]) => {
    const seen = new Set<string>();
    const out: TrueTodoItem["targetUsers"] = [];
    for (const x of [...(a || []), ...(b || [])]) {
      const k = String((x as any)?.userKey || "");
      if (!k) continue;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(x);
    }
    return out;
  };

  for (const it of items || []) {
    const key = String(it?.key || "").trim();
    if (!key) continue;

    const cur = map.get(key);
    if (!cur) {
      map.set(key, it);
      continue;
    }

    const a = priority[it.source] ?? 0;
    const b = priority[cur.source] ?? 0;

    if (a > b) {
      map.set(key, { ...it, targetUsers: mergeTargets(cur.targetUsers, it.targetUsers) });
      continue;
    }

    if (a < b) {
      map.set(key, { ...cur, targetUsers: mergeTargets(cur.targetUsers, it.targetUsers) });
      continue;
    }

    // same priority: keep newer updatedAt if possible
    const itTime = (it as any)?.updatedAt?.toMillis?.() ?? (it as any)?.updatedAt ?? 0;
    const curTime = (cur as any)?.updatedAt?.toMillis?.() ?? (cur as any)?.updatedAt ?? 0;

    if (itTime && curTime && itTime > curTime) {
      map.set(key, { ...it, targetUsers: mergeTargets(cur.targetUsers, it.targetUsers) });
      continue;
    }

    // otherwise, keep current but fill missing
    map.set(key, {
      ...cur,
      title: cur.title || it.title,
      module: cur.module || it.module,
      status: cur.status || it.status,
      docNo: cur.docNo || it.docNo,
      docCode: cur.docCode || it.docCode,
      createdAt: cur.createdAt || it.createdAt,
      updatedAt: cur.updatedAt || it.updatedAt,
      raw: cur.raw || it.raw,
      targetUsers: mergeTargets(cur.targetUsers, it.targetUsers),
    });
  }

  return Array.from(map.values());
}
